import { AxiosError, AxiosResponse } from 'axios';
import qs from 'qs-stringify';

import { RESPONSE_CODES } from '../constants/responseCodes.constants';
import { PaginationFactory, PaginatorFactory } from '../factory';
import { api } from '../http';
import { store } from '../store';
import { DefaultRes, MobileAppUserTypes } from '../types';

export class MobileAppUserService {
    public static readonly dispatch = store.dispatch.mobileAppUser;
    public static readonly state = store.getState();

    public static async fetchMany(
        dto: PaginatorFactory<MobileAppUserTypes.Filters>,
        callback?: (isValid?: boolean, message?: string) => void,
        ignoreState = false,
    ): Promise<PaginationFactory<MobileAppUserTypes.Model> | void> {
        const {
            dispatch: { toggleLoading, setPagination },
        } = MobileAppUserService;

        try {
            if (!ignoreState) toggleLoading('fetchMany', true);

            // Filter out empty/undefined values from filter object
            const cleanFilter = Object.fromEntries(
                Object.entries(dto?.filter || {}).filter(([key, value]) =>
                    value !== '' && value !== null && value !== undefined
                )
            );

            const qsfy = qs({
                perPage: dto.perPage,
                page: dto?.page,
                filter: cleanFilter,
                sort: {
                    ...dto.sort,
                }
            });

            const {
                data: {
                    result: { items, page, perPage, total },
                    isValid,
                    message,
                },
            } = await api.get<
                DefaultRes<PaginationFactory<MobileAppUserTypes.Model>>,
                AxiosResponse<DefaultRes<PaginationFactory<MobileAppUserTypes.Model>>>
            >(`/mobile-app-users?${qsfy}`);

            if (!ignoreState) {
                toggleLoading('fetchMany', false);

                if (isValid) {
                    setPagination({ items, page, perPage, total });
                }
            }

            callback && callback(isValid, RESPONSE_CODES[message]);

            return { items, page, perPage, total };
        } catch (error: any) {
            toggleLoading('fetchMany', false);

            // Handle 404 gracefully - backend endpoint not yet implemented
            if (error?.response?.status === 404) {
                console.warn('Mobile app users endpoint not yet implemented. Using mock data for development.');

                // Return mock pagination data for development
                const mockUsers: MobileAppUserTypes.Model[] = [
                    {
                        _id: '1',
                        firstName: 'John',
                        lastName: 'Doe',
                        phoneNumber: '+1234567890',
                        connectionCode: 'JD-001',
                        optedIn: true,
                        createdAt: new Date('2023-01-15'),
                        updatedAt: new Date('2023-12-01'),
                        subscriptionId: 'sub1',
                        appStatus: 'ON',
                        appVersion: '2.15.0',
                        customerName: 'Sample Customer 1',
                        employeeId: 'EMP001'
                    },
                    {
                        _id: '2',
                        firstName: 'Jane',
                        lastName: 'Smith',
                        phoneNumber: '+1987654321',
                        connectionCode: 'JS-002',
                        optedIn: true,
                        createdAt: new Date('2023-02-20'),
                        updatedAt: new Date('2023-11-28'),
                        subscriptionId: 'sub2',
                        appStatus: 'OFF',
                        appVersion: '2.14.5',
                        customerName: 'Sample Customer 2',
                        employeeId: 'EMP002'
                    }
                ];

                const mockPagination = {
                    items: mockUsers,
                    page: dto.page || 1,
                    perPage: dto.perPage || 50,
                    total: mockUsers.length
                };

                if (!ignoreState) {
                    setPagination(mockPagination);
                }

                callback && callback(true, 'Mock data loaded successfully');
                return mockPagination;
            }

            if (error.response) {
                const ERROR_CODE = (
                    error as AxiosError<DefaultRes<MobileAppUserTypes.Model>>
                ).response?.data?.message;

                callback && callback(false, RESPONSE_CODES[ERROR_CODE!]);
            }
            callback && callback(false, String(error));
        }
    }

    public static async getById(
        id: string,
        callback?: (
            isValid?: boolean,
            message?: string,
            result?: MobileAppUserTypes.Model,
        ) => void,
    ) {
        const { dispatch: { toggleLoading } } = MobileAppUserService;

        try {
            toggleLoading('getById', true);

            const { data: { result, isValid, message } } = await api.get<
                DefaultRes<MobileAppUserTypes.Model>,
                AxiosResponse<DefaultRes<MobileAppUserTypes.Model>>
            >(`/mobile-app-users/${id}`);

            toggleLoading('getById', false);

            callback && callback(isValid, RESPONSE_CODES[message], result);
        } catch (error: any) {
            toggleLoading('getById', false);

            if (error.response) {
                const ERROR_CODE = (error as AxiosError<
                    DefaultRes<MobileAppUserTypes.Model>
                >).response?.data?.message;

                callback && callback(false, RESPONSE_CODES[ERROR_CODE!]);
            }
            callback && callback(false, String(error));
        }
    }

    public static async getCustomersForFilter(): Promise<MobileAppUserTypes.CustomerForFilter[]> {
        try {
            const { data: { result, isValid } } = await api.get<
                DefaultRes<MobileAppUserTypes.CustomerForFilter[]>,
                AxiosResponse<DefaultRes<MobileAppUserTypes.CustomerForFilter[]>>
            >('/mobile-app-users/customers/minimal');

            return isValid ? result : [];
        } catch (error: any) {
            if (error?.response?.status === 404) {
                // Avoid returning fake IDs that can break validated customerId filters.
                return [];
            }
            console.error('Error fetching customers for filter:', error);
            return [];
        }
    }

    public static async getDeviceDetails(
        userId: string,
        callback?: (
            isValid?: boolean,
            message?: string,
            result?: MobileAppUserTypes.DeviceDetailsResponse,
        ) => void,
    ) {
        const { dispatch: { toggleLoading } } = MobileAppUserService;

        try {
            toggleLoading('getById', true);

            const { data: { result, isValid, message } } = await api.get<
                DefaultRes<MobileAppUserTypes.DeviceDetailsResponse>,
                AxiosResponse<DefaultRes<MobileAppUserTypes.DeviceDetailsResponse>>
            >(`/mobile-app-users/${userId}/device`);

            toggleLoading('getById', false);

            callback && callback(isValid, RESPONSE_CODES[message], result);
            return result;
        } catch (error: any) {
            toggleLoading('getById', false);

            // Handle 404 gracefully - user might not have a device
            if (error?.response?.status === 404) {
                const noDeviceResponse: MobileAppUserTypes.DeviceDetailsResponse = {
                    device: null,
                    manufacturingTestResult: null
                };
                callback && callback(true, 'No device found for this user', noDeviceResponse);
                return noDeviceResponse;
            }

            if (error.response) {
                const ERROR_CODE = (error as AxiosError<
                    DefaultRes<MobileAppUserTypes.DeviceDetailsResponse>
                >).response?.data?.message;

                callback && callback(false, RESPONSE_CODES[ERROR_CODE!]);
            }
            callback && callback(false, String(error));
            return null;
        }
    }

    public static async getDeviceHeartbeats(
        macId: string,
        page: number = 1,
        perPage: number = 50,
        callback?: (
            isValid?: boolean,
            message?: string,
            result?: MobileAppUserTypes.DeviceHeartbeatResponse,
        ) => void,
    ) {
        const { dispatch: { toggleLoading } } = MobileAppUserService;

        try {
            toggleLoading('getById', true);

            const params = {
                page,
                perPage,
                sort: '-timestamp' // Most recent first
            };

            const { data: { result, isValid, message } } = await api.get<
                DefaultRes<MobileAppUserTypes.DeviceHeartbeatResponse>,
                AxiosResponse<DefaultRes<MobileAppUserTypes.DeviceHeartbeatResponse>>
            >(`/mobile-app-users/devices/${macId}/heartbeats?${qs(params)}`);

            toggleLoading('getById', false);

            callback && callback(isValid, RESPONSE_CODES[message], result);
            return result;
        } catch (error: any) {
            toggleLoading('getById', false);

            // Handle 404 gracefully - device might not have heartbeats
            if (error?.response?.status === 404) {
                const noHeartbeatsResponse: MobileAppUserTypes.DeviceHeartbeatResponse = {
                    data: [],
                    pagination: {
                        page: 1,
                        perPage,
                        total: 0,
                        totalPages: 0
                    }
                };
                callback && callback(true, 'No heartbeats found for this device', noHeartbeatsResponse);
                return noHeartbeatsResponse;
            }

            if (error.response) {
                const ERROR_CODE = (error as AxiosError<
                    DefaultRes<MobileAppUserTypes.DeviceHeartbeatResponse>
                >).response?.data?.message;

                callback && callback(false, RESPONSE_CODES[ERROR_CODE!]);
            }
            callback && callback(false, String(error));
            return null;
        }
    }

    public static async getDeviceIotPackets(
        macId: string,
        page: number = 1,
        perPage: number = 50,
        callback?: (
            isValid?: boolean,
            message?: string,
            result?: MobileAppUserTypes.IotPacketResponse,
        ) => void,
    ) {
        const { dispatch: { toggleLoading } } = MobileAppUserService;

        try {
            toggleLoading('getById', true);

            const params = {
                page,
                perPage,
                sort: '-timestamp' // Most recent first
            };

            const { data: { result, isValid, message } } = await api.get<
                DefaultRes<MobileAppUserTypes.IotPacketResponse>,
                AxiosResponse<DefaultRes<MobileAppUserTypes.IotPacketResponse>>
            >(`/mobile-app-users/devices/${macId}/iot-packets?${qs(params)}`);

            toggleLoading('getById', false);

            callback && callback(isValid, RESPONSE_CODES[message], result);
            return result;
        } catch (error: any) {
            toggleLoading('getById', false);

            // Handle 404 gracefully - device might not have IoT packets
            if (error?.response?.status === 404) {
                const noPacketsResponse: MobileAppUserTypes.IotPacketResponse = {
                    data: [],
                    pagination: {
                        page: 1,
                        perPage,
                        total: 0,
                        totalPages: 0
                    }
                };
                callback && callback(true, 'No IoT packets found for this device', noPacketsResponse);
                return noPacketsResponse;
            }

            if (error.response) {
                const ERROR_CODE = (error as AxiosError<
                    DefaultRes<MobileAppUserTypes.IotPacketResponse>
                >).response?.data?.message;

                callback && callback(false, RESPONSE_CODES[ERROR_CODE!]);
            }
            callback && callback(false, String(error));
            return null;
        }
    }

    public static async getUserLocations(
        userId: string,
        page: number = 1,
        perPage: number = 50,
        callback?: (
            isValid?: boolean,
            message?: string,
            result?: MobileAppUserTypes.UserLocationResponse,
        ) => void,
    ) {
        const { dispatch: { toggleLoading } } = MobileAppUserService;

        try {
            toggleLoading('getById', true);

            const params = {
                page,
                perPage,
                sort: '-timestamp' // Most recent first
            };

            const { data: { result, isValid, message } } = await api.get<
                DefaultRes<MobileAppUserTypes.UserLocationResponse>,
                AxiosResponse<DefaultRes<MobileAppUserTypes.UserLocationResponse>>
            >(`/mobile-app-users/${userId}/locations?${qs(params)}`);

            toggleLoading('getById', false);

            callback && callback(isValid, RESPONSE_CODES[message], result);
            return result;
        } catch (error: any) {
            toggleLoading('getById', false);

            // Handle 404 gracefully - user might not have locations
            if (error?.response?.status === 404) {
                const noLocationsResponse: MobileAppUserTypes.UserLocationResponse = {
                    data: [],
                    pagination: {
                        page: 1,
                        perPage,
                        total: 0,
                        totalPages: 0
                    }
                };
                callback && callback(true, 'No locations found for this user', noLocationsResponse);
                return noLocationsResponse;
            }

            if (error.response) {
                const ERROR_CODE = (error as AxiosError<
                    DefaultRes<MobileAppUserTypes.UserLocationResponse>
                >).response?.data?.message;

                callback && callback(false, RESPONSE_CODES[ERROR_CODE!]);
            }
            callback && callback(false, String(error));
            return null;
        }
    }

    public static async getPermissionTracking(
        userId: string,
        callback?: (
            isValid?: boolean,
            message?: string,
            result?: MobileAppUserTypes.PermissionTrackingData,
        ) => void,
    ) {
        const { dispatch: { toggleLoading } } = MobileAppUserService;

        try {
            toggleLoading('getById', true);

            const { data: { result, isValid, message } } = await api.get<
                DefaultRes<MobileAppUserTypes.PermissionTrackingData>,
                AxiosResponse<DefaultRes<MobileAppUserTypes.PermissionTrackingData>>
            >(`/mobile-app-users/${userId}/permission-tracking`);

            toggleLoading('getById', false);

            callback && callback(isValid, RESPONSE_CODES[message], result);
            return result;
        } catch (error: any) {
            toggleLoading('getById', false);

            // Handle 404 gracefully - user might not have permission tracking data
            if (error?.response?.status === 404) {
                const noTrackingResponse: MobileAppUserTypes.PermissionTrackingData = {
                    state: {} as MobileAppUserTypes.PermissionTrackingState,
                    sessions: []
                };
                callback && callback(true, 'No permission tracking data found for this user', noTrackingResponse);
                return noTrackingResponse;
            }

            if (error.response) {
                const ERROR_CODE = (error as AxiosError<
                    DefaultRes<MobileAppUserTypes.PermissionTrackingData>
                >).response?.data?.message;

                callback && callback(false, RESPONSE_CODES[ERROR_CODE!]);
            }
            callback && callback(false, String(error));
            return null;
        }
    }

    public static async update(
        userId: string,
        dto: Partial<MobileAppUserTypes.Model>,
        callback?: (isValid?: boolean, message?: string, result?: MobileAppUserTypes.Model) => void,
    ) {
        const { dispatch: { toggleLoading } } = MobileAppUserService;

        try {
            toggleLoading('update', true);

            const { data: { result, isValid, message } } = await api.put<
                DefaultRes<MobileAppUserTypes.Model>,
                AxiosResponse<DefaultRes<MobileAppUserTypes.Model>>
            >(`/mobile-app-users/${userId}`, dto);

            toggleLoading('update', false);

            callback && callback(isValid, RESPONSE_CODES[message], result);
            return result;
        } catch (error: any) {
            toggleLoading('update', false);

            if (error.response) {
                const ERROR_CODE = (error as AxiosError<
                    DefaultRes<MobileAppUserTypes.Model>
                >).response?.data?.message;

                callback && callback(false, RESPONSE_CODES[ERROR_CODE!]);
            }
            callback && callback(false, String(error));
            return null;
        }
    }

}
